import { isHSE } from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/utils";
import { t } from "i18next";
import Documents from "./Documents";
import Messages from "./Messages";
import ServiceDetails from "./ServiceDetails";
import { STATUS_KEYS } from "./statusKeys";
import StatusLog from "./StatusLog";

type BadgeType = "success" | "warning" | "error";

const LANG_BASE = "vessel-call.port-services.preview";
interface StatusMappingEntry {
  label: string;
  badgeType: BadgeType;
  canBeSelected: boolean;
  actionPermission: string | null;
}

export { STATUS_KEYS } from "./statusKeys";

export const USER_TYPES: Record<string, string> = {
  SHIPPING_AGENT: "Shipping Agent",
  MARINE_DEPARTMENT: "MD",
  OPERATIONS: "Operations",
  HSE: "HSE",
};

export const STATUS_MAP: Record<string, StatusMappingEntry> = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    label: "Pending Approval",
    badgeType: "warning",
    canBeSelected: false,
    actionPermission: null,
  },
  [STATUS_KEYS.REQUESTED]: {
    label: "Pending Approval",
    badgeType: "warning",
    canBeSelected: false,
    actionPermission: null,
  },
  [STATUS_KEYS.TERMINAL_APPROVED]: {
    label: "Approved by Terminal",
    badgeType: "success",
    canBeSelected: true,
    actionPermission: "canApprovePortService",
  },
  [STATUS_KEYS.OPERATIONS_APPROVED]: {
    label: "Approved by Operations",
    badgeType: "success",
    canBeSelected: true,
    actionPermission: "canApprovePortService",
  },
  [STATUS_KEYS.HSE_APPROVED]: {
    label: "Approved by HSE",
    badgeType: "success",
    canBeSelected: true,
    actionPermission: "canApprovePortService",
  },
  [STATUS_KEYS.MD_APPROVED]: {
    label: "Approved by Marine Department",
    badgeType: "success",
    canBeSelected: true,
    actionPermission: "canApprovePortService",
  },
  [STATUS_KEYS.STARTED]: {
    label: "Started",
    badgeType: "success",
    canBeSelected: true,
    actionPermission: "canStartPortService",
  },
  [STATUS_KEYS.REJECTED]: {
    label: "Rejected",
    badgeType: "error",
    canBeSelected: true,
    actionPermission: "canRejectPortService",
  },
  [STATUS_KEYS.CLOSED]: {
    label: "Closed",
    badgeType: "success",
    canBeSelected: true,
    actionPermission: "canClosePortService",
  },
  [STATUS_KEYS.CANCELLED]: {
    label: "Cancelled",
    badgeType: "error",
    canBeSelected: false,
    actionPermission: null,
  },
  [STATUS_KEYS.COMPLETED]: {
    label: "Closed",
    badgeType: "success",
    canBeSelected: false,
    actionPermission: null,
  },
};

export const NOTIFY_LIST_MAP: { [key: string]: string } = {
  "Shipping Agent": "SHIPPING_AGENT",
  "FOTT": "FOTT",
  "Dry Bulk": "OPERATIONS",
  "HSE": "HSE",
  "Marine Operator": "MARINE_OPERATOR",
  "Marine Supervisor": "MARINE_SUPERVISOR",
  "Marine Office": "MARINE_OFFICE",
  "Harbour Master": "HARBOUR_MASTER",
};

export const HSE_PTW_CATEGORY_LABEL = "HSE Permit to Work";

export const HSE_PTW_STATUS_LABEL_OVERRIDES: Record<string, string> = {
  [STATUS_KEYS.PENDING_APPROVAL]: "NOC Pending",
  [STATUS_KEYS.HSE_APPROVED]: "NOC Issued",
};

export function getStatusInfo(rawStatus?: string): { label: string; badgeType: BadgeType } {
  const entry = rawStatus ? STATUS_MAP[rawStatus] : undefined;
  return { label: entry?.label || "-", badgeType: entry?.badgeType || "warning" };
}

export function getPortServiceCategoryLabel(serviceCategory?: string): string {
  return isHSE(serviceCategory ?? "") ? HSE_PTW_CATEGORY_LABEL : (serviceCategory ?? "");
}

export function getPortServiceStatusInfo(
  rawStatus?: string,
  serviceCategory?: string,
): { label: string; badgeType: BadgeType } {
  const info = getStatusInfo(rawStatus);
  if (rawStatus && isHSE(serviceCategory ?? "")) {
    const override = HSE_PTW_STATUS_LABEL_OVERRIDES[rawStatus];
    if (override) {
      return { ...info, label: override };
    }
  }
  return info;
}

export const generatePrefixedNotifyList = (action: string, notifyList: string[]) => {
  const base = "PORT_SERVICES";
  return notifyList.map((n) => `${base}_${action}_${NOTIFY_LIST_MAP[n]}`);
};

export const NOTIFY_LIST = Object.keys(NOTIFY_LIST_MAP).map((key) => ({ label: key }));

export function getPortServicePreviewData() {
  const tabs = [
    {
      label: t(`${LANG_BASE}.tabs.service-details`),
      component: ServiceDetails,
      testId: "port-service-preview-service-details",
    },
    {
      label: t(`${LANG_BASE}.tabs.documents`),
      component: Documents,
      testId: "port-service-preview-documents",
    },
    {
      label: t(`${LANG_BASE}.tabs.messages`),
      component: Messages,
      testId: "port-service-preview-messages",
    },
    {
      label: t(`${LANG_BASE}.tabs.status-log`),
      component: StatusLog,
      testId: "port-service-preview-status-log",
    },
  ];
  return { tabs };
}

export type PortServicePreviewData = ReturnType<typeof getPortServicePreviewData>;
